import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, setDisplay, toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { createHtmlElement, empty } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { ColorHex, asColorRgb, isValidHexColor, rgbToHex } from "@cervval.js/common/src/tools/color.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { Picker, PickerOptions } from "../picker";

/** Parameters for DatePicker */
export interface ColorPickerOptions extends PickerOptions {

    /** Default number of columns in picker */
    columnsCount: number;

    /** Enable recent colors section */
    enableRecentColors: boolean;

    /** Default colors available in picker */
    pickerColors: ColorHex[];

}

/** Parameters for DatePicker */
export const defaultColorPickerOptions: ColorPickerOptions = {

    /** Default number of columns in picker */
    columnsCount: 8,

    /** Enable recent colors section */
    enableRecentColors: false,

    /** Default colors available in picker */
    pickerColors: [
        "#d32f2f", "#c62828", "#b71c1c", "#ff8a80", "#ff5252", "#ff1744", "#d50000", "#e91e63",
        "#ad1457", "#880e4f", "#ff80ab", "#ff4081", "#f50057", "#c51162", "#9c27b0", "#7986cb",
        "#2196f3", "#1e88e5", "#1976d2", "#1565c0", "#0d47a1", "#82b1ff", "#448aff", "#2979ff",
        "#aed581", "#9ccc65", "#8bc34a", "#7cb342", "#689f38", "#558b2f", "#33691e", "#ccff90",
        "#ffeb3b", "#fdd835", "#fbc02d", "#f9a825", "#f57f17", "#ffff8d", "#ffff00", "#ffea00",
        "#d84315", "#bf360c", "#ff9e80", "#ff6e40", "#ff3d00", "#dd2c00", "#795548", "#efebe9",
        "#d7ccc8", "#bcaaa4", "#a1887f", "#8d6e63", "#6d4c41", "#5d4037", "#4e342e", "#3e2723",
        "#000000", "#9e9e9e", "#e0e0e0", "#eeeeee", "#efefef", "#f5f5f5", "#f3f3f3", "#ffffff"
    ],

    /** @inheritdoc */
    disabled: false
};


const DIV_DIMENSION = 30;

@CustomElement({
    name: "cjs-color-picker",
    style: require("./colorPicker.scss"),
    template: require("./colorPicker.html")
})
export class CjsColorPicker extends Picker<ColorHex> {

    @TypedWebComponentOptions(defaultColorPickerOptions)
    public override readonly options!: WebComponentOptions<ColorPickerOptions>;

    @TypedHtmlElements()
    private readonly _components!: {
        colorPickerContainer: HTMLElement,
        colorsContainer: HTMLElement,
        colorPickerRecentContainer: HTMLElement,
        colorPickerRecentColorsContainer: HTMLElement
    };

    /** Recent colors picked by user, component lifecycle cache */
    private _recentColors: ColorHex[] = [];

    constructor(options?: Partial<ColorPickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Init component after it is inserted in the dom
     */
    public override init(): void {
        // Init listeners
        this._initListeners();
    }

    /** Init containers listeners */
    protected _initListeners(): void {

        // Disable click listeners for container div
        this.addEventListener("click", e => e.stopPropagation());

    }

    /** Refresh component display */
    public override refresh(): void {
        super.refresh();
        this._displayOptions();

        // Set picker width from number of columns
        this._components.colorPickerContainer.style.width = toPx(this.options.columnsCount * DIV_DIMENSION);

    }

    /**
     * Set selected color value
     */
    public override setValue(color: ColorHex | undefined): void {
        this._value = color;

        if (this._value != null) {
            // Check isn't given color is in colors list in options, remove it from color grid
            if (!this.options.pickerColors.map(color => color.toLocaleLowerCase()).includes(this._value.toLocaleLowerCase())) {
                this._recentColors.unshift(this._value);
                this._displayOptions();
            } else {
                this._highlightSelected();
            }
        }
    }

    /**
     * Highlight selected color
     */
    private _highlightSelected(): void {

        if (this._value != null) {

            // Get all colorsDiv from DOM
            const options = Array.from(this.getContainer().querySelectorAll("div.color-option")) as HTMLElement[];

            // For all colors, toggle selected class
            for (const htmlElement of options) {
                const bgColor = htmlElement.style.backgroundColor;
                const isColorSelected = rgbToHex(asColorRgb(bgColor))?.toLocaleLowerCase() === this._value.toLocaleLowerCase();
                toggleClass(htmlElement, isColorSelected, "color-selected");
            }
        }

    }

    /**
     * Add color option div
     */
    private _addOption(component: HTMLElement, color: ColorHex, row: number, col: number): void {
        const colorDiv = createHtmlElement("div", {
            cssClass: ["color-option"],
            style: {
                backgroundColor: color,
                gridColumn: String(col),        // Set grid position for cell
                gridRow: String(row)        // Set grid position for cell
            },
            attributes: {
                title: color,
                tabindex: "0"
            }
        });
        colorDiv.addEventListener("click", () => {
            if (this.disabled) { return; }// Don't allow click if component is disabled
            this._onColorDivClick(color);
        });
        colorDiv.addEventListener("keydown", (event) => {
            if (event.key === "Enter") {
                if (this.disabled) { return; }// Don't allow click if component is disabled
                this._onColorDivClick(color);
            }
        });


        component.append(colorDiv);
    }

    /** When a colorOption cell is clicked */
    private _onColorDivClick(color: ColorHex): void {
        this.setValue(color);
        this._dispatchOnPickerValueChange();

        // If color isn't already added to recentColors, unshift it
        if (!this._recentColors.includes(color)) { this._recentColors.unshift(color); }
        else {
            // Else: color is already added, remove it from array, and unshift it
            this._recentColors = this._recentColors.filter(recentColor => recentColor !== color);
            this._recentColors.unshift(color);
        }

        this._displayRecentColorsOptions();
        this._highlightSelected();
    }

    /**
     * Show options
     */
    private _displayOptions(): void {
        // Empty container
        empty(this._components.colorsContainer);

        let row = 1;
        let col = 1;

        // For all colors in options
        for (let i = 0; i < this.options.pickerColors.length; i++) {

            // Check color correct hexa format
            if (!isValidHexColor(this.options.pickerColors[i])) { continue; }

            // Add colorOption div
            this._addOption(this._components.colorsContainer, this.options.pickerColors[i], row, col);
            col++;
            if (col % (this.options.columnsCount + 1) === 0) {
                row++;
                col = 1;
            }

        }

        this._displayRecentColorsOptions();

        this._highlightSelected();
    }

    /** Display recent colors options div */
    private _displayRecentColorsOptions(): void {
        if (this._recentColors.length && this.options.enableRecentColors) {

            setDisplay(this._components.colorPickerRecentContainer, Display.GRID);
            empty(this._components.colorPickerRecentColorsContainer);

            const row = 1;
            let col = 1;

            // For all recentColors, add colorOptions - Don't add div if the length of recent colors if superior to grid columns
            for (let i = 0; i < (this._recentColors.length > this.options.columnsCount ? this.options.columnsCount : this._recentColors.length); i++) {
                const color = this._recentColors[i];
                // Check if color is valid
                if (!isValidHexColor(color)) { continue; }
                this._addOption(this._components.colorPickerRecentColorsContainer, color, row, col);
                col++;
            }
        } else {
            setDisplay(this._components.colorPickerRecentContainer, Display.NONE);
        }

    }

}